import { ChangeEvent, useCallback, useEffect, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { SelectChangeEvent } from "@mui/material";
import dayjs from "dayjs";

import { errorNormalizer, validateName } from "../../../shared/Helpers/functions";
import { CustomInput, CustomSelect, MultipleSelect, Text } from "../../../shared/uiComponents";
import { useResetCalendarData } from "../../Calendar/Helpers/hooks";

import { SignatureInput, SoapNoteButtons, TimeInfo } from "../components";
import { useIsActionAllowed } from "../helpers";
import {
  ACTIONS,
  ERRORS,
  initialMeetAndGreetErrorState,
  initialMeetAndGreetState,
  MeetAndGreetErrorReducer,
  meetAndGreetReducer,
} from "./reducer";
import { useMeetAndGreetOptions } from "../hook";

import { DispatchProperties, useSelector } from "../../../redux/store";
import { setDate, setEndTime, setStartTime } from "../../../redux/State/clientSlice/sessionSlice";
import { getSoapNote, updateMeetAndGreet } from "../../../redux/State/clientSlice/soapNoteSlice";
import {
  MeetAndGreetProperties,
  UpdateMeetAndGreetProperties,
} from "../../../redux/API/ClientAPIHelpers/soapNoteProperties";

const MeetAndGreet = () => {
  const todayString = dayjs().format("MM/DD/YYYY");

  const dispatch = useDispatch<DispatchProperties>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetData } = useResetCalendarData();
  const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
  const isMounted = useRef(true);

  const { barriersList, participantsList, goalAddressedList } = useMeetAndGreetOptions();

  const reportId = params.get("reportId");
  const clientId = params.get("clientId");
  const sessionId = params.get("sessionId");
  const forcedOperation = params.get("forcedOperation") === "true" ? true : false;

  const soapNoteStatus = useSelector((state) => state.soapNote.soapNote?.status?.status);
  const userId = useSelector((state) => state.soapNote.soapNote?.user?.id);
  const { user } = useSelector((state) => state.session.sessionInfo);
  const sessionInfo = useSelector((state) => state.session.sessionInfo);
  const locationList = useSelector((state) => state.soapNote.locations);
  const errorList = useSelector((state) => state.soapNote.error?.errors);

  const [data, setData] = useReducer(meetAndGreetReducer, initialMeetAndGreetState);

  const disabled = !useIsActionAllowed(soapNoteStatus, userId);

  const [errors, setErrors] = useReducer(MeetAndGreetErrorReducer, initialMeetAndGreetErrorState);

  useEffect(() => {
    if (!!data.participantResponses.length) return;
    const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
    setData({ type: ACTIONS.setParticipants, payload });
  }, [participantsList, data, setData]);

  useEffect(() => {
    if (!errorList) return;
    const list = Object.keys(errorList);
    list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
  }, [errorList]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (currentUpdateRequestRef.current) {
        console.log("Aborting request on unmount");
        currentUpdateRequestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!reportId || !clientId || !sessionId) return;

    dispatch(getSoapNote({ reportId, clientId }))
      .then(unwrapResult)
      .then((response) => {
        const {
          location,
          date,
          startTime,
          endTime,
          participantResponses,
          barrierToTreatmentResponses,
        } = response as MeetAndGreetProperties;
        const barriers = barrierToTreatmentResponses?.map((x) => x.id);
        const participants = participantResponses?.map((x) => x.id);

        dispatch(setDate(dayjs(date).format()));
        dispatch(setStartTime(dayjs(startTime).format()));
        dispatch(setEndTime(dayjs(endTime).format()));

        setData({
          type: ACTIONS.setState,
          payload: response,
        });

        setData({
          type: ACTIONS.setBarriers,
          payload: barriers,
        });

        setData({
          type: ACTIONS.setParticipants,
          payload: participants,
        });

        if (!!location) {
          setData({
            type: ACTIONS.setLocationId,
            payload: location.id,
          });
        }
      })
      .catch(errorNormalizer);
  }, [dispatch, clientId, reportId, sessionId]);

  const updateSoapNoteHandler = useCallback(
    (submit: boolean) => {
      if (currentUpdateRequestRef.current) {
        console.log("Aborting previous pending request");
        currentUpdateRequestRef.current.abort();
      }
      if (!reportId || !clientId || !data) return;
      const { date, startTime, endTime } = sessionInfo;
      const allData: UpdateMeetAndGreetProperties = {
        ...data,
        date,
        startTime,
        endTime,
        reportId,
        submit,
        forcedOperation,
        clientId,
      };

      const requestPromise = dispatch(updateMeetAndGreet(allData));
      currentUpdateRequestRef.current = requestPromise;

      requestPromise
        .then(unwrapResult)
        .then(() => {
          if (!submit || !isMounted.current) return;
          resetData();
          toast("Success");
          navigate("/calendar");
        })
        .catch(errorNormalizer)
        .finally(() => {
          if (currentUpdateRequestRef.current === requestPromise) {
            currentUpdateRequestRef.current = null;
          }
        });
    },
    [reportId, clientId, forcedOperation, data, sessionInfo, navigate, resetData, dispatch]
  );

  const locationHandler = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setLocationId });
    if (!errors.LocationId) return;
    setErrors({ payload: false, type: ERRORS.LocationId });
  };

  const barriersHandler = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    if (typeof value === "string") return;
    setData({ payload: value, type: ACTIONS.setBarriers });
    if (!errors.BarriersResponses) return;
    setErrors({ payload: false, type: ERRORS.BarriersResponses });
  };

  // const goalAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
  //   const { value } = event.target;
  //   setData({ payload: value, type: ACTIONS.setGoalsAddressed });
  //   if (!errors.GoalsAddressed) return;
  //   setErrors({ payload: false, type: ERRORS.GoalsAddressed });
  // };

  const participantsHandler = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    if (typeof value === "string") return;
    setData({ type: ACTIONS.setParticipants, payload: value });
    if (!errors.ParticipantResponses) return;
    setErrors({ payload: false, type: ERRORS.ParticipantResponses });
  };

  const sessionSummaryHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setSessionSummary });
    if (!errors.SessionSummary) return;
    setErrors({ payload: false, type: ERRORS.SessionSummary });
  };

  const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
    if (!errors.Signature) return;
    setErrors({ payload: false, type: ERRORS.Signature });
  };

  const barriersOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setBarriersOther });
    if (!errors.BarriersOther) return;
    setErrors({ payload: false, type: ERRORS.BarriersOther });
  };

  const validateSignatureHandler = () => {
    if (validateName(data.signature)) {
      saveHandler();
      return;
    }
    setErrors({ payload: true, type: ERRORS.Signature });
  };

  const isOtherSelectedForBarriersToTreatment = data.barrierToTreatmentResponses?.find(
    (x) => barriersList.find((y) => y.id === x)?.text === "Other"
  );

  const saveHandler = useCallback(() => updateSoapNoteHandler(false), [updateSoapNoteHandler]);
  const submitHandler = useCallback(() => updateSoapNoteHandler(true), [updateSoapNoteHandler]);

  return (
    <>
      <TimeInfo disabled={disabled} save={saveHandler} />
      <CustomInput
        label="Behavior Technician:"
        value={user.fullName}
        setValue={() => {}}
        className="marginBottom16"
        disabled={true}
      />
      {/* {!!supervisor && (
        <div className="padding8" style={{ minWidth: "300px" }}>
          <InfoRow>
            <Text title={"Supervisor:"} size="smallBold" />
            <Text title={supervisor.fullName} />
          </InfoRow>
        </div>
      )} */}
      <CustomSelect
        label="Location:"
        data={locationList}
        value={!!data.locationId ? data.locationId : ""}
        setValue={locationHandler}
        className="marginBottom16"
        disabled={disabled}
        onBlur={saveHandler}
        error={errors.LocationId}
      />
      <MultipleSelect
        label="Session Participants:"
        data={participantsList}
        initialValue={data.participantResponses}
        setValue={participantsHandler}
        error={errors.ParticipantResponses}
        className="marginBottom16"
        disabled={disabled}
        onBlur={saveHandler}
      />
      <MultipleSelect
        label="Goals Addressed:"
        data={goalAddressedList}
        setValue={()=>{}}
        className={"marginBottom16"}
        initialValue={goalAddressedList.map(x => x.id)}
        disabled={true}
      />
      <MultipleSelect
        label="Barriers to Treatment:"
        data={barriersList}
        setValue={barriersHandler}
        className={"marginBottom16"}
        initialValue={data.barrierToTreatmentResponses}
        disabled={disabled}
        onBlur={saveHandler}
      />
      {isOtherSelectedForBarriersToTreatment && (
        <CustomInput
          label="If other, please describe: "
          value={data.barrierToTreatmentOther}
          setValue={barriersOtherHandler}
          className="marginBottom16"
          multiline={{
            multiline: true,
            rowCount: 3,
          }}
          disabled={disabled}
          onBlur={saveHandler}
          error={errors.BarriersOther}
        />
      )}
      <CustomInput
        label="Session Summary: "
        value={data.sessionSummary}
        setValue={sessionSummaryHandler}
        className="marginBottom16"
        multiline={{
          multiline: true,
          rowCount: 3,
        }}
        disabled={disabled}
        onBlur={saveHandler}
      />
      <div style={{ width: "250px" }}>
        <CustomInput
          label="Printed Name:"
          value={data.printedName.toUpperCase()}
          setValue={() => {}}
          disabled={true}
          className="marginBottom8 signature"
        />
        <SignatureInput
          error={errors.Signature}
          value={data.signature}
          setValue={signatureHandler}
          disabled={disabled}
          onBlur={validateSignatureHandler}
        />
      </div>
      <div>
        <Text title="Date of signature: " size="smallBold" />
        <Text title={todayString} />
      </div>
      <SoapNoteButtons save={saveHandler} submit={submitHandler} />
    </>
  );
};

export default MeetAndGreet;
